import Skeleton from '@cvent/carina/components/Skeleton';
import {Card} from '../card/Card';
import {useSkeletonDelay} from './useSkeletonDelay';

/**
 * Skeleton for Stripe-wrapped billing pages (BillingPage, UpgradePage, DowngradePage, etc.)
 * Two-column layout: billing form on left, plan summary on right
 * Includes a 300ms delay to prevent flash on fast-loading pages
 */
export const StripeSkeleton = () => {
  const show = useSkeletonDelay();

  if (!show) {
    return null;
  }

  return (
    <div className="flex justify-center items-start gap-6 md:pt-[var(--carina-spacing-12)] pb-8 px-4">
      {/* Left side - Billing form */}
      <div>
        <Card styleClasses="p-4 pt-0 px-6 sm:p-8 sm:pl-6 md:pl-8 w-[700px] box-border max-w-full mb-4">
          {/* Back button */}
          <div className="[margin-bottom:var(--carina-spacing-8)]">
            <Skeleton className="w-[80px] h-[32px] rounded" hasShimmer />
          </div>

          {/* Title */}
          <div className="[margin-bottom:var(--carina-spacing-6)]">
            <Skeleton className="w-[250px] h-[28px] rounded" hasShimmer />
          </div>

          {/* Form fields */}
          <div className="space-y-6">
            {Array.from({length: 5}).map((_, index) => (
              <div key={index}>
                <Skeleton
                  className="w-[120px] h-[16px] rounded mb-2"
                  hasShimmer
                />
                <Skeleton className="w-full h-[40px] rounded" hasShimmer />
              </div>
            ))}
          </div>

          {/* Terms checkbox */}
          <div className="mt-6 flex items-start gap-2">
            <Skeleton className="w-[20px] h-[20px] rounded" hasShimmer />
            <Skeleton className="w-[90%] h-[16px] rounded" hasShimmer />
          </div>
        </Card>
      </div>

      {/* Right side - Plan summary */}
      <div className="self-stretch hidden md:block">
        <div className="sticky top-[var(--carina-spacing-12)] h-fit">
          <Card styleClasses="p-4 pt-0 px-6 sm:p-8 sm:pl-6 md:pl-8 md:pb-4 w-[408px] box-border max-w-full mb-4">
            {/* Plan summary header */}
            <div className="mb-6">
              <Skeleton className="w-[150px] h-[24px] rounded" hasShimmer />
            </div>

            {/* Plan details */}
            <div className="space-y-4 mb-6">
              <div className="flex justify-between">
                <Skeleton className="w-[120px] h-[18px] rounded" hasShimmer />
                <Skeleton className="w-[80px] h-[18px] rounded" hasShimmer />
              </div>
              <Skeleton className="w-full h-[60px] rounded" hasShimmer />
            </div>

            {/* Pricing breakdown */}
            <div className="space-y-3 py-4 border-t border-b">
              {Array.from({length: 3}).map((_, index) => (
                <div key={index} className="flex justify-between">
                  <Skeleton className="w-[100px] h-[16px] rounded" hasShimmer />
                  <Skeleton className="w-[60px] h-[16px] rounded" hasShimmer />
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex justify-between py-4">
              <Skeleton className="w-[120px] h-[20px] rounded" hasShimmer />
              <Skeleton className="w-[80px] h-[20px] rounded" hasShimmer />
            </div>

            {/* Submit button */}
            <Skeleton className="w-full h-[40px] rounded" hasShimmer />

            {/* Footer note */}
            <div className="mt-4 space-y-1">
              <Skeleton className="w-full h-[12px] rounded" hasShimmer />
              <Skeleton className="w-[80%] h-[12px] rounded" hasShimmer />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
